<script>
import { useSettingsStore } from '@/stores/settingsStore'

// 탭 라벨은 i18n 키로 — template 에서 $t(t.labelKey) 로 lookup
//   레거시 DlgSettings 의 map 탭은 노출하지 않음 (지도 base/extent 는 내부 기본값 유지)
const TABS = [
  { key: 'layers',   labelKey: 'dashboard_settings.tab_layers' },
  { key: 'wave',     labelKey: 'dashboard_settings.tab_wave' },
  { key: 'pressure', labelKey: 'dashboard_settings.tab_pressure' },
  { key: 'browser',  labelKey: 'dashboard_settings.tab_browser' },
]

const PRESSURE_VALUE_OPTIONS = [
  { value: 'min', label: 'Min' },
  { value: 'max', label: 'Max' },
  { value: 'avg', label: 'Avg' },
]

export default {
  name: 'SettingsModal',
  props: {
    open: { type: Boolean, default: false },
  },
  emits: ['close', 'apply'],
  setup() {
    return { settings: useSettingsStore() }
  },
  data() {
    return {
      activeTab: 'layers',
      saving: false,
      localError: '',
      TABS,
      PRESSURE_VALUE_OPTIONS,
      layers: [],
      wave: {},
      pressure: {},
      map: { name: 'Bing', type: 'Standard', extent: [0, 0, 0, 0] },
      // Layers 탭 — 전체 체크
      allCheck: true,
    }
  },
  computed: {
    loading() { return this.settings.loading },
    error() { return this.localError || this.settings.error },
  },
  watch: {
    open(v) {
      if (v) this.init()
    },
    layers: {
      deep: true,
      handler(arr) {
        // 모든 layer 가 visible 이면 allCheck=true 동기화
        this.allCheck = arr.every(l => l.visible !== false)
      },
    },
  },
  methods: {
    async init() {
      if (!this.settings.loaded) await this.settings.load()
      this.syncFromStore()
    },
    syncFromStore() {
      this.layers   = this.settings.layers.map(l => ({ ...l }))
      this.wave     = { ...this.settings.wave }
      this.pressure = { ...this.settings.pressure }
      this.map      = {
        ...this.settings.map,
        extent: Array.isArray(this.settings.map?.extent)
          ? [...this.settings.map.extent]
          : [0, 0, 0, 0],
      }
      this.allCheck = this.layers.every(l => l.visible !== false)
    },
    buildPayload() {
      return {
        layers: this.layers.map(l => ({
          key: l.key,
          label: l.label,
          zoom: Number(l.zoom),
          // OSM 컬럼이 편집하는 값 — 지도 fetch/렌더 임계치
          bing_minzoom: Number(l.bing_minzoom ?? l.zoom),
          color: l.color,
          visible: !!l.visible,
        })),
        wave: { ...this.wave },
        pressure: { ...this.pressure },
        map: {
          ...this.map,
          extent: this.map.extent.map(v => Number(v) || 0),
        },
      }
    },
    // 레거시 check_all() — Layer 일괄 토글
    onToggleAll() {
      // allCheck 가 v-model 로 이미 바뀐 상태로 진입 — 그 값으로 모든 layer 동기화
      const next = !!this.allCheck
      this.layers.forEach(l => { l.visible = next })
    },
    // 레거시 handleOk() 의 입력값 검증 — 잘못된 값이면 alert 후 저장 중단
    validate() {
      const wave = this.wave || {}
      const pressure = this.pressure || {}
      if (Number(wave.freqXMax) > 3000) {
        this.localError = 'frequency X 값은 3000 이하로 입력하세요.'
        return false
      }
      if (Number(wave.energyMin) > Number(wave.energyMax)) {
        this.localError = '누수음 최소/최대 사이즈를 확인하세요. (Min ≤ Max)'
        return false
      }
      if (Number(pressure.min) > Number(pressure.max)) {
        this.localError = '압력 Min/Max 값을 확인하세요. (Min ≤ Max)'
        return false
      }
      return true
    },
    async save() {
      this.localError = ''
      if (!this.validate()) return
      this.saving = true
      try {
        const payload = this.buildPayload()
        await this.settings.save(payload)
        this.$emit('apply', payload)
        this.$emit('close')
      } catch (err) {
        this.localError = err?.message || this.$t('common.save_failed')
      } finally {
        this.saving = false
      }
    },
    stopProp(e) { e.stopPropagation() },
  },
}
</script>

<template>
  <!-- body 로 Teleport — HeaderBar/MapToolbar 의 stacking context 영향 안 받게 -->
  <Teleport to="body">
    <transition name="fade">
      <div v-if="open" class="modal-backdrop" @click="$emit('close')">
      <div class="modal-card" @click="stopProp">
        <header class="modal-header">
          <h3>{{ $t('dashboard_settings.title') }}</h3>
          <button class="modal-close" @click="$emit('close')" :title="$t('common.close')">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </header>

        <nav class="modal-tabs">
          <button v-for="t in TABS" :key="t.key"
            :class="['tab-btn', activeTab === t.key ? 'active' : '']"
            @click="activeTab = t.key">{{ $t(t.labelKey) }}</button>
        </nav>

        <div class="modal-body">
          <div v-if="loading" class="muted">{{ $t('common.loading') }}</div>
          <div v-else-if="error" class="error-msg">{{ error }}</div>

          <!-- Map (배경지도 / 타입 / extent) -->
          <div v-if="!loading && activeTab === 'map'" class="form-sections">
            <section class="form-section">
              <h4>Background Map</h4>
              <div class="form-row">
                <label>Map</label>
                <div class="radio-row">
                  <label class="radio"><input type="radio" v-model="map.name" value="Daum"/> Daum</label>
                  <label class="radio"><input type="radio" v-model="map.name" value="Bing"/> Bing</label>
                </div>
              </div>
              <div class="form-row">
                <label>Type</label>
                <div class="radio-row">
                  <label class="radio"><input type="radio" v-model="map.type" value="Standard"/> Standard</label>
                  <label class="radio"><input type="radio" v-model="map.type" value="Satellite"/> Satellite</label>
                </div>
              </div>
            </section>
            <section class="form-section">
              <h4>Extent (EPSG:4326)</h4>
              <div class="form-row">
                <label>MinX</label>
                <input type="number" step="0.001" v-model.number="map.extent[0]" class="num-input wide"/>
              </div>
              <div class="form-row">
                <label>MinY</label>
                <input type="number" step="0.001" v-model.number="map.extent[1]" class="num-input wide"/>
              </div>
              <div class="form-row">
                <label>MaxX</label>
                <input type="number" step="0.001" v-model.number="map.extent[2]" class="num-input wide"/>
              </div>
              <div class="form-row">
                <label>MaxY</label>
                <input type="number" step="0.001" v-model.number="map.extent[3]" class="num-input wide"/>
              </div>
            </section>
          </div>

          <!-- Layers -->
          <table v-if="!loading && activeTab === 'layers'" class="layer-table">
            <thead>
              <tr>
                <th style="width: 2.5rem;">
                  <input type="checkbox" v-model="allCheck" @change="onToggleAll" :title="allCheck ? '전체 해제' : '전체 선택'"/>
                </th>
                <th>{{ $t('dashboard_settings.col_layer') }}</th>
                <th style="width: 6rem;">{{ $t('dashboard_settings.col_zoom') }}</th>
                <th style="width: 5rem;">{{ $t('dashboard_settings.col_color') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="l in layers" :key="l.key">
                <td><input type="checkbox" v-model="l.visible"/></td>
                <td>{{ l.label }}</td>
                <td>
                  <!-- OSM(=bing_minzoom) 임계치 — 지도 fetch/렌더 가시 임계 -->
                  <input type="number" min="1" max="22" v-model.number="l.bing_minzoom" class="num-input"/>
                </td>
                <td><input type="color" v-model="l.color" class="color-input"/></td>
              </tr>
            </tbody>
          </table>

          <!-- Wave -->
          <div v-if="!loading && activeTab === 'wave'" class="form-sections">
            <section class="form-section">
              <h4>{{ $t('dashboard_settings.freq_chart') }}</h4>
              <div class="form-row">
                <label>{{ $t('dashboard_settings.freq_x_max') }}</label>
                <input type="number" v-model.number="wave.freqXMax" class="num-input wide"/>
              </div>
              <div class="form-row">
                <label>{{ $t('dashboard_settings.freq_y_max') }}</label>
                <input type="number" v-model.number="wave.freqYMax" class="num-input wide"/>
              </div>
            </section>

            <section class="form-section">
              <h4>{{ $t('dashboard_settings.wave_energy') }}</h4>
              <div class="form-row">
                <label>{{ $t('dashboard_settings.energy_min') }}</label>
                <input type="number" v-model.number="wave.energyMin" class="num-input wide"/>
              </div>
              <div class="form-row">
                <label>{{ $t('dashboard_settings.energy_max') }}</label>
                <input type="number" v-model.number="wave.energyMax" class="num-input wide"/>
              </div>
              <div class="form-row">
                <label>{{ $t('dashboard_settings.energy_fixed') }}</label>
                <input type="number" v-model.number="wave.energyFixed" class="num-input wide"/>
              </div>
            </section>

            <section class="form-section">
              <h4>{{ $t('dashboard_settings.map_color') }}</h4>
              <div class="form-row">
                <label>{{ $t('dashboard_settings.leak') }}</label>
                <input type="color" v-model="wave.leakColor" class="color-input"/>
              </div>
              <div class="form-row">
                <label>{{ $t('dashboard_settings.other_color') }}</label>
                <input type="color" v-model="wave.otherColor" class="color-input"/>
              </div>
              <div class="form-row">
                <label>Show wave value</label>
                <input type="checkbox" v-model="wave.valueVisible"/>
              </div>
            </section>
          </div>

          <!-- Pressure -->
          <div v-if="!loading && activeTab === 'pressure'" class="form-sections">
            <section class="form-section">
              <h4>{{ $t('dashboard_settings.pressure_range') }}</h4>
              <div class="form-row">
                <label>Value</label>
                <select v-model="pressure.value" class="form-select">
                  <option v-for="o in PRESSURE_VALUE_OPTIONS" :key="o.value" :value="o.value">{{ o.label }}</option>
                </select>
              </div>
              <div class="form-row">
                <label>{{ $t('dashboard_settings.min') }}</label>
                <input type="number" step="0.1" v-model.number="pressure.min" class="num-input wide"/>
              </div>
              <div class="form-row">
                <label>{{ $t('dashboard_settings.max') }}</label>
                <input type="number" step="0.1" v-model.number="pressure.max" class="num-input wide"/>
              </div>
              <div class="form-row">
                <label>{{ $t('dashboard_settings.map_color') }}</label>
                <input type="color" v-model="pressure.color" class="color-input"/>
              </div>
              <div class="form-row">
                <label>Show pressure value</label>
                <input type="checkbox" v-model="pressure.valueVisible"/>
              </div>
            </section>
          </div>

          <!-- Browser — 브라우저 캐시/하드웨어 가속 안내 (레거시 안내 화면 대체) -->
          <div v-if="!loading && activeTab === 'browser'" class="form-sections">
            <section class="form-section">
              <h4>※ Chrome setting</h4>
              <p class="hint">Chrome → Settings → Advanced → System</p>
              <p class="hint dim">하드웨어 가속을 켜면 지도/차트 렌더링이 부드러워집니다.</p>
            </section>
            <section class="form-section">
              <h4>※ MS Edge setting</h4>
              <p class="hint">Microsoft Edge → Settings → System</p>
              <p class="hint dim">하드웨어 가속을 켜면 지도/차트 렌더링이 부드러워집니다.</p>
            </section>
          </div>
        </div>

        <footer class="modal-footer">
          <button class="btn-cancel" :disabled="saving" @click="$emit('close')">{{ $t('common.cancel') }}</button>
          <button class="btn-save" :disabled="loading || saving" @click="save">
            {{ saving ? $t('common.saving') : $t('common.save') }}
          </button>
        </footer>
      </div>
    </div>
  </transition>
  </Teleport>
</template>

<style scoped>
.modal-backdrop {
  position: fixed; inset: 0;
  background: var(--overlay-bg);
  backdrop-filter: blur(3px);
  display: flex; align-items: center; justify-content: center;
  /* 다른 디테일 모달(9000~9999) 보다 위로 올라오게 — 범례/툴바(1000) 위는 자동 보장 */
  z-index: 10000;
  font-family: Pretendard, sans-serif;
}

.modal-card {
  width: 34rem; max-width: 92vw;
  max-height: 82vh;
  background: var(--bg-surface);
  border: 1px solid var(--border-subtle);
  border-radius: 0.625rem;
  display: flex; flex-direction: column;
  box-shadow: 0 20px 60px var(--shadow-elev-strong);
}

.modal-header {
  display: flex; align-items: center;
  padding: 0.875rem 1rem;
  border-bottom: 1px solid var(--border-subtle);
}
.modal-header h3 {
  flex: 1; margin: 0;
  font-size: 1rem; font-weight: 700; color: var(--text-primary); letter-spacing: .02em;
}
.modal-close {
  width: 1.75rem; height: 1.75rem;
  display: flex; align-items: center; justify-content: center;
  background: transparent; border: 1px solid var(--border-subtle);
  border-radius: 0.375rem; color: var(--text-muted); cursor: pointer;
  transition: color .15s, background .15s;
}
.modal-close:hover { color: var(--status-err); background: rgba(var(--status-err-rgb),.08); }

.modal-tabs {
  display: flex;
  border-bottom: 1px solid var(--border-subtle);
  background: var(--bg-surface-2);
}
.tab-btn {
  flex: 1; padding: 0.625rem 0;
  background: transparent; border: none;
  color: var(--text-disabled); font-size: 0.8125rem; font-weight: 600;
  letter-spacing: .04em; cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: color .15s, border-color .15s, background .15s;
}
.tab-btn:hover { color: var(--text-secondary); }
.tab-btn.active { color: var(--accent); border-bottom-color: var(--accent); background: rgba(var(--accent-rgb),.04); }

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 0.875rem 1rem;
}
.muted { font-size: 0.8125rem; color: var(--text-disabled); padding: 0.75rem 0; text-align: center; }
.error-msg {
  font-size: 0.8125rem;
  color: var(--status-err);
  padding: 0.5rem 0.75rem;
  background: rgba(var(--status-err-rgb),.08);
  border: 1px solid rgba(var(--status-err-rgb),.3);
  border-radius: 0.375rem;
}

.layer-table { width: 100%; border-collapse: collapse; font-size: 0.8125rem; }
.layer-table th, .layer-table td {
  padding: 0.5rem 0.625rem; text-align: left;
  border-bottom: 1px solid var(--border-subtle);
}
.layer-table thead th {
  color: var(--text-disabled); font-weight: 600;
  letter-spacing: .04em; text-transform: uppercase;
  font-size: 0.6875rem;
}
.layer-table tbody td { color: var(--text-secondary); }
.layer-table tbody tr:hover { background: rgba(var(--accent-rgb),.04); }

.form-sections { display: flex; flex-direction: column; gap: 1rem; }
.form-section {
  background: var(--bg-surface-3);
  border: 1px solid var(--border-subtle);
  border-radius: 0.5rem;
  padding: 0.75rem 0.875rem;
}
.form-section h4 {
  margin: 0 0 0.625rem 0;
  font-size: 0.75rem; font-weight: 700;
  color: var(--accent); letter-spacing: .06em; text-transform: uppercase;
}
.form-row {
  display: grid;
  grid-template-columns: 10rem 1fr;
  align-items: center;
  gap: 0.5rem;
  padding: 4px 0;
}
.form-row label {
  font-size: 0.8125rem; color: var(--text-secondary);
}

.num-input {
  width: 4rem;
  /*background: #081020;*/
  border: 1px solid var(--border-subtle);
  color: var(--text-primary); font-size: 0.8125rem;
  padding: 4px 8px;
  border-radius: 0.25rem; outline: none;
  transition: border-color .15s;
}
.num-input.wide { width: 7rem; }
.num-input:focus { border-color: rgba(var(--accent-rgb),.5); }

.color-input {
  width: 2.5rem; height: 1.5rem;
  padding: 0;
  background: transparent;
  border: 1px solid var(--border-subtle);
  border-radius: 0.25rem; cursor: pointer;
}
.color-input::-webkit-color-swatch { border: none; border-radius: 2px; }
.color-input::-webkit-color-swatch-wrapper { padding: 2px; }

/* Map 탭 — Daum/Bing, Standard/Satellite 라디오 */
.radio-row { display: flex; gap: 1rem; align-items: center; }
.radio {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.8125rem;
  color: var(--text-secondary);
  cursor: pointer;
}
.radio input { margin: 0; cursor: pointer; }

/* Pressure value 셀렉트 */
.form-select {
  padding: 4px 8px;
  font-size: 0.8125rem;
  background: var(--bg-surface-2);
  color: var(--text-primary);
  border: 1px solid var(--border-subtle);
  border-radius: 0.25rem;
  outline: none;
  min-width: 6rem;
  cursor: pointer;
}
.form-select:focus { border-color: rgba(var(--accent-rgb),.5); }

/* Browser 탭 안내 */
.hint {
  font-size: 0.8125rem;
  color: var(--text-secondary);
  margin: 0 0 4px 0;
}
.hint.dim { color: var(--text-disabled); font-size: 0.75rem; }

.modal-footer {
  display: flex; justify-content: flex-end; gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-top: 1px solid var(--border-subtle);
}
.btn-cancel, .btn-save {
  padding: 6px 16px;
  font-size: 0.8125rem; font-weight: 600; letter-spacing: .02em;
  border-radius: 0.375rem; cursor: pointer;
  transition: background .15s, border-color .15s, opacity .15s;
}
.btn-cancel {
  background: transparent;
  border: 1px solid var(--border-subtle); color: var(--text-muted);
}
.btn-cancel:hover:not(:disabled) { background: var(--bg-surface-2); color: var(--text-secondary); }
.btn-save {
  background: var(--accent);
  border: 1px solid transparent; color: #fff;
}
.btn-save:hover:not(:disabled) { background: var(--accent-hover); }
.btn-save:disabled, .btn-cancel:disabled { opacity: .5; cursor: not-allowed; }

.fade-enter-active, .fade-leave-active { transition: opacity .18s; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
</style>
